/**
 * Frühwarnung (Spec Verbleib): the risk is shown as three levels, not as a percentage. A level is an order among the children,
 * no forecast. high = the top share of the scores of the model, raised = the next share; the shares are a setting (QM).
 */
export type RiskLevel = 'low' | 'raised' | 'high';

export type RiskLevelShares = { highPercent: number; raisedPercent: number };

export const DEFAULT_RISK_LEVELS: RiskLevelShares = {highPercent: 10, raisedPercent: 20};
/** Shares come in these steps: the model stores the score at every step */
export const RISK_LEVEL_STEP = 5;
export const RISK_LEVEL_MAX_PERCENT = 50;

export const RISK_LEVEL_TEXT: Record<RiskLevel, string> = {low: 'niedrig', raised: 'erhöht', high: 'hoch'};

/** Lowest score of the levels */
export type RiskLevelCuts = { high: number; raised: number };

type ModelCuts = { threshold: number; cuts?: Record<string, number> };

/** Score at the top share; models from before the levels only know their threshold (top 10 %) */
function cutAt(model: ModelCuts, percent: number, fallback: number): number {
    return model.cuts?.[String(percent)] ?? fallback;
}

export function riskLevelCuts(model: ModelCuts, shares: Partial<RiskLevelShares> | null | undefined): RiskLevelCuts {
    const highPercent = shares?.highPercent ?? DEFAULT_RISK_LEVELS.highPercent;
    const raisedPercent = shares?.raisedPercent ?? DEFAULT_RISK_LEVELS.raisedPercent;
    const high = cutAt(model, highPercent, model.threshold);
    return {high, raised: Math.min(high, cutAt(model, highPercent + raisedPercent, model.threshold / 2))};
}

export function riskLevelOf(score: number, cuts: RiskLevelCuts): RiskLevel {
    return score >= cuts.high ? 'high' : score >= cuts.raised ? 'raised' : 'low';
}

/** Valid shares: steps of 5, high + raised at most 50 % */
export function riskLevelSharesError(shares: RiskLevelShares): string | null {
    const {highPercent, raisedPercent} = shares;
    const step = (value: number) => Number.isInteger(value) && value > 0 && value % RISK_LEVEL_STEP === 0;
    if (!step(highPercent) || !step(raisedPercent)) {
        return `Die Anteile gehen in Schritten von ${RISK_LEVEL_STEP} %.`;
    }
    return highPercent + raisedPercent > RISK_LEVEL_MAX_PERCENT ? `Beide Stufen zusammen höchstens ${RISK_LEVEL_MAX_PERCENT} %.` : null;
}
